import { FileText, Network, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'
import { Card } from '@/components/ui/card'

export default function DocsPage() {
  return (
    <DocsLayout>
      <DocsContent
        title="Welcome to XENDREL"
        description="XENDREL is an intelligence analysis platform that transforms unstructured documents into actionable knowledge graphs, enabling investigators to uncover hidden connections and patterns."
        nextPage={{ title: 'Quick Start', href: '/docs/getting-started' }}
      >
        <H2 id="what-is-spectre">What is XENDREL?</H2>
        <p className="text-foreground mb-4 leading-relaxed">
          XENDREL is a powerful investigation platform designed for analysts, researchers, and
          investigators who need to process large volumes of documents and discover relationships
          between entities.
        </p>
        <p className="text-foreground mb-6 leading-relaxed">
          The platform uses advanced AI to automatically extract entities (people, organizations,
          locations, dates, etc.) from your documents and build a knowledge graph that reveals
          connections you might otherwise miss.
        </p>

        <H2 id="key-concepts">Key Concepts</H2>

        <div className="grid gap-4 my-6">
          <ConceptCard
            icon={FileText}
            title="Cases"
            description="Cases are containers for your investigations. Each case holds documents, extracted entities, and the knowledge graph built from your evidence."
          />
          <ConceptCard
            icon={FileText}
            title="Documents"
            description="Upload PDFs, Word documents, spreadsheets, and even identity documents. XENDREL processes them to extract structured information."
          />
          <ConceptCard
            icon={Network}
            title="Knowledge Graph"
            description="A visual network showing entities and their relationships. Navigate connections, find paths between entities, and discover hidden patterns."
          />
          <ConceptCard
            icon={Users}
            title="Entities"
            description="People, organizations, locations, phone numbers, emails, and other key data points extracted from your documents."
          />
        </div>

        <H2 id="how-it-works">How It Works</H2>

        <H3 id="step-1-create-case">1. Create a Case</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Start by creating a case for your investigation. Give it a descriptive name and optionally
          add notes about the investigation scope.
        </p>

        <H3 id="step-2-upload-documents">2. Upload Documents</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Add documents to your case. XENDREL supports PDFs, Word documents (DOCX), spreadsheets
          (CSV, XLSX), and identity documents (passports, IDs). Each document is processed by an AI
          pipeline that extracts relevant entities.
        </p>

        <H3 id="step-3-explore-graph">3. Explore the Graph</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Once documents are processed, explore the knowledge graph. Click on entities to see their
          details and connections. Use the structured view for a table-based exploration or the
          graph view for visual analysis.
        </p>

        <H3 id="step-4-query-and-analyze">4. Query and Analyze</H3>
        <p className="text-foreground mb-6 leading-relaxed">
          Ask questions in natural language like "Who is connected to John Smith?" or "Show me all
          transactions over $10,000". XENDREL translates your questions into graph queries and
          returns relevant results.
        </p>

        <DocsCallout variant="tip" title="Getting Started">
          Ready to begin? Check out the{' '}
          <Link
            to="/docs/getting-started"
            className="text-amber-600 dark:text-amber-400 hover:text-amber-300 underline"
          >
            Quick Start guide
          </Link>{' '}
          to create your first case and process your first document.
        </DocsCallout>

        <H2 id="core-features">Core Features</H2>

        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Automatic Entity Extraction</strong> - AI-powered
              extraction of people, organizations, locations, dates, and more from unstructured
              documents.
            </span>
          </li>
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Knowledge Graph Visualization</strong> -
              Interactive graph showing entities and their relationships with filtering and
              path-finding capabilities.
            </span>
          </li>
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Natural Language Queries</strong> - Ask questions
              in plain English and get relevant entities and connections.
            </span>
          </li>
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity Resolution</strong> - Merge duplicate
              entities that refer to the same person or organization.
            </span>
          </li>
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Identity Document Processing</strong> - Extract
              structured data from passports, driver's licenses, and other ID documents.
            </span>
          </li>
          <li className="flex items-start gap-3 text-foreground">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Geolocation Mapping</strong> - View extracted
              locations on an interactive map.
            </span>
          </li>
        </ul>
      </DocsContent>
    </DocsLayout>
  )
}

function ConceptCard({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof FileText
  title: string
  description: string
}) {
  return (
    <Card size="compact" className="flex flex-row gap-4 rounded-lg p-4 bg-card/50">
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <Icon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-foreground mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Card>
  )
}
